import { memo, useEffect, useState } from 'react'
import {Box, Link, Stack, Typography} from '@mui/material'
import {CRORE, LAKH, LOGOS_BASE_URL, MAIN_SCREENER_COLUMN} from '../assets/constants'
import CircularSymbol from '../components/common/CircularAvatar'
import commonActions from '../actions/common.actions'
import store from '../store'
import {Operator} from '../proto/generated/screener'
import {toLocaleStringIn} from './general'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CompanyEvents from '../components/CompanyEvents/companyEvents'
import { CompanyEventProto } from '../proto/generated/company_event'
import { CorporateAction } from '../proto/generated/corporate-action'
import { exchangeToJSON } from '../proto/generated/common'
import { getSecurityAsync, getSecurityKey } from './security'
import { Security } from '../proto/generated/security'

const ScreenerMainColumnCell = memo(({
    data, onClick, todayEventsMap, hideSecurityName, corporateActions, displayExchange, exchange
}: {
    data: string[], onClick: Function, todayEventsMap?: Record<string, CompanyEventProto[]>, hideSecurityName?: boolean,
    corporateActions?: Record<string, CorporateAction[]>, displayExchange?: boolean, exchange?: any
}) => {
    const symbol = data[0]
    const [security, setSecurity] = useState<Security | undefined>()
    useEffect(() => {
        if (!exchange || !symbol) return
        getSecurityAsync(getSecurityKey(exchange, symbol)).then(setSecurity)
    }, [exchange, symbol])
    const companyCode = security?.companyCode || symbol
    const todayEvents = todayEventsMap?.[companyCode] || []
    const corporateActionDetails = corporateActions?.[companyCode] || []
    const hasEvent = todayEvents.length > 0 || corporateActionDetails.length > 0
    return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'normal', width: '100%', cursor: 'pointer', flexwrap: 'nowrap'}}>
                    <Box style={{ margin: '8px', marginLeft: '4px', height: '20px', width: '20px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {data[2] && data[2] !== 'null' ? (
                            <img
                                style={{
                                    margin: 0,
                                    height: '20px',
                                    width: '20px',
                                    borderRadius: '50%',
                                    padding: 0
                                }}
                                src={`${LOGOS_BASE_URL}${data[2]}.svg`}
                                alt={data[2]}
                            />
                        ) : (
                            <CircularSymbol
                                marginLeft={0}
                                marginBT={0}
                                marginRight={0}
                                symbol={data[0]}
                                width={'20px'}
                                height={'20px'}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', width: hasEvent ? 'calc(100% - 56px)' : 'calc(100% - 36px)' }}>
                        {data.map((t, i) => {
                            if (i > 1) return null
                            if (i === 1 && !hideSecurityName) return null
                            return (
                                <Link
                                    key={i}
                                    color="inherit"
                                    style={{ cursor: 'pointer' }}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        textDecoration: 'none',
                                        '&:hover': {
                                            color: (theme) => (i === 0 ? theme.palette.info.main : theme.palette.text.disabled)
                                        },
                                        '&:hover .open-new-icon': {
                                            visibility: i === 0 ? 'visible !important' : 'hidden',
                                            color: (theme) => (i === 0 ? theme.palette.info.main : theme.palette.text.disabled)
                                        },
                                        maxWidth: '100%'
                                    }}
                                    underline="none"
                                    onClick={(event) => onClick(event)}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: '100%' }}>
                                        <Typography
                                            title={i === 1 ? t : undefined}
                                            variant={i === 0 ? 'body1' : 'caption'}
                                            color={i === 0 ? 'inherit' : 'grey'}
                                            sx={{
                                                fontSize: i === 0 ? '14px' : '11px',
                                                lineHeight: 1,
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                textOverflow: 'ellipsis',
                                                display: 'block'
                                            }}
                                        >
                                            {t}
                                        </Typography>
                                        {i === 0 && displayExchange && exchange && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: '10px',
                                                    lineHeight: 1,
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    display: 'block'
                                                }}
                                            >
                                                {exchangeToJSON(exchange)}
                                            </Typography>
                                        )}
                                        {i === 0 && (
                                            <OpenInNewIcon
                                                className="open-new-icon"
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'inherit',
                                                    visibility: 'hidden',
                                                    marginLeft: '4px',
                                                }}
                                            />
                                        )}
                                    </Stack>
                                </Link>
                            )
                        })}
                    </Box>
                    {(hasEvent) && (
                        <Box sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', marginRight: '4px' }}>
                                <CompanyEvents companyCode={companyCode} />
                        </Box>
                    )}
                </Box>
            )
})

export const renderSpecialScreenerColumns = (
    columnCode: string,
    data: string[],
    onClick: Function,
    todayEventsMap?: Record<string, CompanyEventProto[]>,
    hideSecurityName?: boolean,
    corporateActions?: Record<string, CorporateAction[]>,
    displayExchange?: boolean,
    exchange?: any,
    dispatch?: any
) => {
    switch (columnCode) {
        case MAIN_SCREENER_COLUMN:
            return <ScreenerMainColumnCell data={data} onClick={onClick} todayEventsMap={todayEventsMap} hideSecurityName={hideSecurityName} corporateActions={corporateActions} displayExchange={displayExchange} exchange={exchange} />
        default:
            return (
                <span>
                    {data.map((t, i) => (
                            <Typography>
                                {t}
                            </Typography>
                        )
                    )}
                </span>
            )
    }
}

export function roundToSmartPrecision(number: number) {
    return number.toFixed(number > 100 ? 0 : (number > 10 ? 1 : 2))
}

export function formatLargeNumber(number: number, precision = -1): string {
    if (number === undefined) return '-'
    let negative = false
    let result: string
    if (number === 0) return '0'
    if (number < 0) {
        negative = true
    }
    number = Math.abs(number)
    if (number >= 1e12) {
        result = (precision !== -1 ? (number / 1e12).toFixed(precision) : roundToSmartPrecision(number / 1e12)) + 'T'
    } else if (number >= 1e9) {
        result = (precision !== -1 ? (number / 1e9).toFixed(precision) : roundToSmartPrecision(number / 1e9)) + 'B'
    } else if (number >= 1e6) {
        result = (precision !== -1 ? (number / 1e6).toFixed(precision) : roundToSmartPrecision(number / 1e6)) + 'M'
    } else if (number >= 1e3) {
        result = (precision !== -1 ? (number / 1e3).toFixed(precision) : roundToSmartPrecision(number / 1e3)) + 'K'
    } else {
        result = (precision !== -1 ? number.toFixed(precision) : roundToSmartPrecision(number)).toString()
    }
    return negative ? '-' + result : result
}

/** OI quantities: 3 significant digits (3.25M, 10.3M). Optional leading + for changes. */
export function formatOiQuantity(value: number, signed = false): string {
    if (!Number.isFinite(value)) return '-'
    const formatted = formatLargeNumber(value)
    if (signed && value > 0) return `+${formatted}`
    return formatted
}

export function formatFundamentalPrice(price: number) {
    if (price >= CRORE) {
        return `${Number((price / CRORE).toFixed(0)).toLocaleString('en-In')} Cr`
    } else if (price >= LAKH) {
        return `${Number((price / LAKH).toFixed(0)).toLocaleString('en-In')} L`
    } else {
        return toLocaleStringIn(price)
    }
}

export function getPrice(price: number) {
    if (price >= CRORE) {
        return `${Number((price / CRORE).toFixed(0)).toLocaleString('en-In')}`
    } else if (price >= LAKH) {
        return `${Number((price / LAKH).toFixed(0)).toLocaleString('en-In')}`
    } else {
        return toLocaleStringIn(price)
    }
}

export function isValidOperator(operator: Operator | undefined): boolean {
    if (!operator) return false
    return ![Operator.OPERATOR_UNKNOWN, Operator.UNRECOGNIZED].includes(operator)
}

export function downloadCSV(fixedData: any[], name: string) {
    // Extract headers from the keys of the first object
    if (fixedData.length > 0) {
        const headers: string[] = Object.keys(fixedData[0])
        // Convert data to CSV format
        const csvContent = 'data:text/csv;charset=utf-8,'
            + [headers.join(',')].concat(fixedData.map((obj: any) => {
                return headers.map((header: string) => {
                    // If the value contains a comma, enclose it in quotes
                    let value = obj[header]
                    if (typeof value === 'string' && value.includes(',')) {
                        value = `"${value}"`
                    }
                    return value
                }).join(',')
            })).join('\n')

        // Create a temporary anchor element
        const encodedUri = encodeURI(csvContent)
        const link = document.createElement('a')
        link.setAttribute('href', encodedUri)
        link.setAttribute('download', `Screen-${name}-Results.csv`)

        // Trigger download
        document.body.appendChild(link)
        link.click()

        // Cleanup
        document.body.removeChild(link)
    } else {
        commonActions.setApiError(store.dispatch, {error: true, message: 'Data is empty', errorType: 'error'})
    }
}

export function moveItemFromToIndex(arr: any [], fromIndex: number, toIndex: number) {
    if (fromIndex < 0 || fromIndex >= arr.length || toIndex < 0 || toIndex >= arr.length) {
        return arr
    }
    const newArr = [...arr]
    const [movedItem] = newArr.splice(fromIndex, 1)
    if(fromIndex>toIndex) {
        newArr.splice(toIndex+1, 0, movedItem)
    } else {
        newArr.splice(toIndex, 0, movedItem)
    }
    return newArr
}